import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle } from 'lucide-react';
import { config } from '@/config';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_TEXT = {
  weekly: "the Monday roundup of the week's stories",
  daily: "each new story as soon as it's published",
};

// The two email footer links, both keyed by ?token=<unsubscribe_token>:
//   /unsubscribe (and /api/unsubscribe, the address older emails used)
//   /email-preferences ("Switch to weekly" / "Switch to daily")
// Loading the page only LOOKS UP the subscriber; nothing changes until the
// person clicks a button, because email link scanners open (and sometimes run
// the JavaScript on) every link in an email.
function EmailPreferences({ mode }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [phase, setPhase] = useState('loading'); // loading | ready | invalid
  const [prefs, setPrefs] = useState(null); // { email, subscribed, frequency }
  const [chosen, setChosen] = useState('weekly');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  // Resubscribing from the unsubscribe page lands on the preferences view.
  const [view, setView] = useState(mode);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    axios.get(`${BACKEND_URL}/api/email-prefs`, { params: { token } })
      .then((resp) => {
        if (cancelled) return;
        setPrefs(resp.data);
        setChosen(resp.data.frequency);
        setPhase('ready');
      })
      .catch(() => { if (!cancelled) setPhase('invalid'); });
    return () => { cancelled = true; };
  }, [token]);

  const post = async (path, body, doneNotice) => {
    setBusy(true);
    setError('');
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/email-prefs/${path}`, { token, ...body });
      setPrefs(resp.data);
      setChosen(resp.data.frequency);
      setNotice(doneNotice(resp.data));
    } catch (err) {
      if (err.response?.status === 404) setPhase('invalid');
      else setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const saveFrequency = (frequency) =>
    post('frequency', { frequency }, (data) => `Saved. You'll get ${FREQUENCY_TEXT[data.frequency]}.`);
  const unsubscribe = () => post('unsubscribe', {}, () => '');
  const resubscribe = () => {
    setView('preferences');
    return post('resubscribe', {}, (data) => `Welcome back! You'll get ${FREQUENCY_TEXT[data.frequency]}.`);
  };

  const linkStyle = { color: '#1a1a1a', fontWeight: '600' };
  const smallStyle = { marginTop: '24px', fontSize: '14px', color: '#666' };
  const textButton = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    color: '#1a1a1a', fontWeight: '600', textDecoration: 'underline', fontSize: '14px'
  };
  const primaryButton = { marginTop: '16px', minWidth: '220px', opacity: busy ? 0.7 : 1 };

  const frequencyOption = (value, title, detail) => (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px', textAlign: 'left', cursor: 'pointer',
      padding: '12px 14px', marginBottom: '8px', borderRadius: '6px',
      border: `1px solid ${chosen === value ? '#1a1a1a' : '#d0d0d0'}`,
      background: chosen === value ? '#f8f8f8' : '#fff'
    }}>
      <input type="radio" name="frequency" value={value} checked={chosen === value}
        disabled={busy} onChange={() => { setChosen(value); setNotice(''); }} style={{ marginTop: '4px' }} />
      <span>
        <strong>{title}</strong>
        <span style={{ display: 'block', fontSize: '14px', color: '#666' }}>{detail}</span>
      </span>
    </label>
  );

  const renderUnsubscribed = () => (
    <>
      <CheckCircle style={{ width: '56px', height: '56px', color: '#28a745', margin: '0 auto 16px' }} />
      <h1 data-testid="unsubscribe-success">You're unsubscribed</h1>
      <p>{prefs.email} won't get any more emails from me.</p>
      <p style={smallStyle}>
        Changed your mind?{' '}
        <button type="button" style={textButton} onClick={resubscribe} disabled={busy}>
          {busy ? 'Saving…' : 'Resubscribe'}
        </button>
      </p>
    </>
  );

  const renderPreferences = () => (
    <>
      <h1>How often would you like to hear from me?</h1>
      <p style={{ marginBottom: '16px', color: '#666' }}>{prefs.email}</p>
      <div style={{ maxWidth: '360px', margin: '0 auto' }}>
        {frequencyOption('weekly', 'Weekly roundup', "Every Monday, the week's new stories in one email.")}
        {frequencyOption('daily', 'Every new story', "Each story in your inbox as soon as it's published.")}
      </div>
      {notice ? (
        <p style={{ marginTop: '12px', color: '#28a745', fontWeight: '600' }} data-testid="email-prefs-saved">{notice}</p>
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => saveFrequency(chosen)}
          disabled={busy || chosen === prefs.frequency}
          style={{ ...primaryButton, opacity: busy || chosen === prefs.frequency ? 0.6 : 1 }}
          data-testid="email-prefs-save"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      )}
      <p style={smallStyle}>
        <Link to={`/unsubscribe?token=${encodeURIComponent(token)}`} style={linkStyle}>Unsubscribe instead</Link>
      </p>
    </>
  );

  const renderUnsubscribe = () => (
    <>
      <h1>Unsubscribe?</h1>
      {notice ? (
        <p style={{ color: '#28a745', fontWeight: '600' }} data-testid="unsubscribe-switched">{notice}</p>
      ) : prefs.frequency === 'daily' ? (
        <div style={{
          maxWidth: '380px', margin: '16px auto 8px', padding: '16px', borderRadius: '8px',
          border: '1px solid #d0d0d0', background: '#f8f8f8'
        }}>
          <p style={{ margin: '0 0 12px', fontWeight: '600' }}>Rather get it weekly?</p>
          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#555' }}>
            One email every Monday with the week's stories, instead of one for each story.
          </p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => saveFrequency('weekly')}
            disabled={busy}
            data-testid="unsubscribe-switch-weekly"
          >
            Switch to weekly
          </button>
        </div>
      ) : null}
      <p style={{ marginTop: '16px' }}>
        {notice ? 'Or, to stop all emails to ' : 'This stops all emails to '}<strong>{prefs.email}</strong>.
      </p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={unsubscribe}
        disabled={busy}
        style={primaryButton}
        data-testid="unsubscribe-confirm"
      >
        {busy ? 'Saving…' : 'Unsubscribe'}
      </button>
    </>
  );

  return (
    <div className="public-page" style={{
      backgroundImage: `url(${config.backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      <div className="public-container" style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        textAlign: 'center'
      }}>
        {phase === 'loading' ? (
          <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        ) : phase === 'invalid' ? (
          <div data-testid="unsubscribe-error">
            <XCircle style={{ width: '56px', height: '56px', color: '#999', margin: '0 auto 16px' }} />
            <h1>Hmm, that link didn't work</h1>
            <p>It may be old or got cut off in your email.</p>
            <p style={{ marginTop: '16px' }}>
              Just reply to any of my emails and I'll take care of it for you.
            </p>
          </div>
        ) : (
          <>
            {!prefs.subscribed ? renderUnsubscribed()
              : view === 'unsubscribe' ? renderUnsubscribe()
              : renderPreferences()}
            {error && <p style={{ marginTop: '12px', color: '#dc3545' }}>{error}</p>}
            <p style={smallStyle}><Link to="/" style={linkStyle}>Back to the site</Link></p>
          </>
        )}
      </div>
    </div>
  );
}

export default EmailPreferences;
